<script setup lang="ts">
import { ArrowRight, Check, Github, LoaderCircle, Mail } from "lucide-vue-next"
import { onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"

import { ApiError, apiRequest, apiUrl } from "../lib/api"
import { useSessionStore } from "../stores/session"

const router = useRouter()
const route = useRoute()
const session = useSessionStore()
const email = ref("")
const code = ref("")
const verificationId = ref("")
const developmentCode = ref<string | null>(null)
const errorMessage = ref("")
const providers = ref({ email: false, github: false, google: false })

const oauthErrors: Record<string, string> = {
  cancelled: "登录已取消，你可以重新选择登录方式。",
  configuration: "该登录方式尚未完成配置。",
  invalid_state: "登录请求已失效，请重新开始。",
  provider: "第三方账号验证失败，请稍后重试。",
  account_disabled: "此账户已停用，请联系支持人员。",
}

onMounted(async () => {
  const oauthError = typeof route.query.oauth_error === "string" ? route.query.oauth_error : ""
  if (oauthError) errorMessage.value = oauthErrors[oauthError] ?? "登录失败，请重新尝试。"
  try {
    providers.value = await apiRequest<{ email: boolean; github: boolean; google: boolean }>(
      "/v1/auth/oauth/providers",
    )
  } catch {
    // The existing bootstrap error already explains API availability.
  }
})

function oauthSignIn(provider: "github" | "google"): void {
  const returnTo = safeRedirect()
  const query = returnTo === "/" ? "" : `?return_to=${encodeURIComponent(returnTo)}`
  window.location.assign(apiUrl(`/v1/auth/oauth/${provider}/start${query}`))
}

function safeRedirect(): string {
  const redirect = typeof route.query.redirect === "string" ? route.query.redirect : ""
  return redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : "/"
}

async function requestCode(): Promise<void> {
  errorMessage.value = ""
  try {
    const result = await session.startEmailSignIn(email.value)
    verificationId.value = result.verification_id
    developmentCode.value = result.development_code
    if (result.development_code) code.value = result.development_code
  } catch (error) {
    errorMessage.value = error instanceof ApiError ? error.message : "验证码发送失败，请稍后重试。"
  }
}

async function verify(): Promise<void> {
  errorMessage.value = ""
  try {
    await session.verifyEmail(verificationId.value, code.value)
    await router.replace(safeRedirect())
  } catch (error) {
    errorMessage.value = error instanceof ApiError ? error.message : "登录失败，请检查验证码。"
  }
}
</script>

<template>
  <main class="login-page" data-screen-label="账户登录">
    <section class="login-intro">
      <RouterLink to="/" class="brand brand--light">
        <span class="brand__icon" aria-hidden="true"></span>
        <span class="brand__word"><strong>ByreCC</strong><small>牛马看世界</small></span>
      </RouterLink>
      <div class="login-intro__content">
        <span class="signal-label signal-label--dark"><i></i>Agents see the world</span>
        <h1>牛马，<br /><em>看世界。</em></h1>
        <p>一个账户，连接世界。</p>
      </div>
      <div class="login-proof">
        <span><Check :size="15" />一条命令完成接入</span>
        <span><Check :size="15" />默认只读与最小权限</span>
        <span><Check :size="15" />连接能力持续扩展</span>
      </div>
    </section>

    <section class="login-panel">
      <div class="login-form">
        <div>
          <span class="eyebrow">{{ verificationId ? "Step 02 / 02" : "Step 01 / 02" }}</span>
          <h2>{{ verificationId ? "输入验证码" : "登录 ByreCC" }}</h2>
          <p>
            {{ verificationId ? `验证码已发送至 ${email}` : "新用户登录后会自动获得 Workspace 和 20 可用积分。" }}
          </p>
        </div>

        <form v-if="!verificationId && providers.email" class="form-stack" @submit.prevent="requestCode">
          <label class="field">
            <span>工作邮箱</span>
            <span class="input-wrap"><Mail :size="17" /><input v-model="email" type="email" autocomplete="email" required placeholder="you@example.com" /></span>
          </label>
          <button class="primary-button full-button" :disabled="session.loading">
            <LoaderCircle v-if="session.loading" class="spin" :size="17" />
            <span>发送验证码</span><ArrowRight v-if="!session.loading" :size="17" />
          </button>
        </form>

        <form v-else-if="verificationId" class="form-stack" @submit.prevent="verify">
          <label class="field">
            <span>六位验证码</span>
            <input v-model="code" class="code-input" inputmode="numeric" maxlength="6" required pattern="\d{6}" autocomplete="one-time-code" />
          </label>
          <p v-if="developmentCode" class="dev-note">开发环境验证码：<code>{{ developmentCode }}</code></p>
          <button class="primary-button full-button" :disabled="session.loading || code.length !== 6">
            <LoaderCircle v-if="session.loading" class="spin" :size="17" />
            <span>进入控制台</span><ArrowRight v-if="!session.loading" :size="17" />
          </button>
          <button type="button" class="text-button" @click="verificationId = ''; code = ''">更换邮箱</button>
        </form>

        <p v-if="errorMessage || session.bootstrapError" class="form-error" role="alert">
          {{ errorMessage || session.bootstrapError }}
        </p>

        <div v-if="!verificationId && providers.email" class="divider"><span>或</span></div>
        <div v-if="!verificationId" class="oauth-buttons">
          <button
            class="secondary-button full-button"
            :disabled="!providers.github"
            :title="providers.github ? '使用 GitHub 账户继续' : 'GitHub OAuth 尚未配置'"
            @click="oauthSignIn('github')"
          >
            <Github :size="17" /><span>使用 GitHub 登录</span><small v-if="!providers.github">未配置</small>
          </button>
          <button
            class="secondary-button full-button"
            :disabled="!providers.google"
            :title="providers.google ? '使用 Google 账户继续' : 'Google OAuth 尚未配置'"
            @click="oauthSignIn('google')"
          >
            <span class="oauth-mark" aria-hidden="true">G</span><span>使用 Google 登录</span><small v-if="!providers.google">未配置</small>
          </button>
        </div>
        <p class="legal-copy">继续即表示你同意服务条款与隐私政策。</p>
      </div>
    </section>
  </main>
</template>
